import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/app/Navbar";
import Footer from "@/components/app/Footer";
import ListingCard from "@/components/app/ListingCard";
import { api, buildWhatsAppLink } from "@/lib/api";
import { ArrowRight, Sparkles, MessageCircle } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1759671953272-a4719ca32266?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBtaW5pbWFsJTIwYXBhcnRtZW50JTIwZXh0ZXJpb3J8ZW58MHx8fHwxNzgwNzI0NjY4fDA&ixlib=rb-4.1.0&q=85";

export default function Landing() {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/listings?limit=6").then(({ data }) => setListings(data)).finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen">
      <Navbar />

      {/* HERO */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 lg:pt-24 pb-20 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-medium animate-fade-up"
                 style={{ background: "rgba(34,60,51,0.08)", color: "var(--forest)" }}>
              <Sparkles className="w-3.5 h-3.5" /> Now Available in Kharghar
            </div>
            <h1 className="mt-6 font-serif-display text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight animate-fade-up" style={{ animationDelay: "100ms" }}>
              Long-Stay Homes
              <br />
              <span style={{ color: "var(--terracotta)" }}>for Modern</span> Travelers.
            </h1>
            <p className="mt-6 max-w-xl text-base sm:text-lg leading-relaxed animate-fade-up" style={{ color: "var(--text-2)", animationDelay: "200ms" }}>
              Beautifully curated spaces crafted for remote workers, professionals, and long-term travelers seeking comfort, flexibility, and luxury living — with a bike, a cook, or a clean home, only if you want.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4 animate-fade-up" style={{ animationDelay: "300ms" }}>
              <Link to="/listings" className="btn-pill btn-primary" data-testid="cta-explore">
                Explore Stays <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/dashboard" className="btn-pill btn-outline-dark" data-testid="cta-host">
                List your home
              </Link>
            </div>
          </div>

          <div className="lg:col-span-5 relative animate-fade-up" style={{ animationDelay: "400ms" }}>
            <div className="relative rounded-3xl overflow-hidden surface-card p-0">
              <img src={HERO_IMG} alt="Luxury long-stay home" className="w-full h-[520px] object-cover" />
              <div className="absolute bottom-5 left-5 right-5 rounded-2xl px-5 py-4 backdrop-blur-xl"
                   style={{ background: "rgba(248,246,240,0.85)" }}>
                <div className="overline">Curated by HummingStayz</div>
                <div className="font-serif-display text-xl mt-1" style={{ color: "var(--forest)" }}>
                  Premium spaces. Thoughtful add-ons.
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="border-y" style={{ borderColor: "var(--line)", background: "var(--surface-alt)" }} data-testid="stats">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12 grid grid-cols-2 lg:grid-cols-4 gap-8">
          {[
            ["10+", "Luxury Homes"],
            ["100+", "Happy Guests"],
            ["24/7", "Guest Support"],
            ["4.9★", "Average Rating"],
          ].map(([n, l]) => (
            <div key={l} className="flex flex-col">
              <div className="font-serif-display text-4xl lg:text-5xl" style={{ color: "var(--forest)" }}>{n}</div>
              <div className="text-sm mt-1" style={{ color: "var(--text-2)" }}>{l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* LISTINGS */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24" data-testid="featured-section">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <div className="overline">Featured Stays</div>
            <h2 className="mt-3 font-serif-display text-4xl sm:text-5xl tracking-tight leading-tight">
              Curated Long-Stay <br /> Experiences.
            </h2>
          </div>
          <p className="max-w-md text-sm" style={{ color: "var(--text-2)" }}>
            Discover premium monthly stays designed for comfort, work, and modern living — each one paired with optional add-ons curated by the host.
          </p>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {[0, 1, 2].map((i) => (
              <div key={i} className="surface-card h-[500px] animate-pulse" />
            ))}
          </div>
        ) : listings.length === 0 ? (
          <div className="surface-card p-12 text-center" data-testid="no-listings">
            <p className="font-serif-display text-2xl mb-2">No stays listed yet.</p>
            <p className="text-sm mb-6" style={{ color: "var(--text-2)" }}>
              Be the first host to curate a HummingStayz home.
            </p>
            <Link to="/dashboard" className="btn-pill btn-primary inline-flex">List your home</Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="featured-grid">
            {listings.map((l, i) => <ListingCard key={l.id} listing={l} index={i} />)}
          </div>
        )}

        <div className="mt-12 flex justify-center">
          <Link to="/listings" className="btn-pill btn-outline-dark" data-testid="see-all-link">
            See all stays <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20" data-testid="cta-section">
        <div className="relative rounded-3xl overflow-hidden p-10 lg:p-16" style={{ background: "var(--forest)", color: "#fff" }}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div>
              <h2 className="font-serif-display text-4xl sm:text-5xl leading-tight tracking-tight">
                Find your next <br />
                <span style={{ color: "var(--terracotta)" }}>long-stay home.</span>
              </h2>
              <p className="mt-5 text-sm sm:text-base" style={{ color: "rgba(255,255,255,0.7)" }}>
                Premium spaces. Flexible stays. Exceptional comfort — with optional bike, cook, cleaning and more.
              </p>
            </div>
            <div className="flex flex-wrap gap-3 lg:justify-end">
              <a
                href={buildWhatsAppLink("Hello HummingStayz! I'd like to book a long-stay home.")}
                target="_blank" rel="noopener noreferrer"
                className="btn-pill"
                style={{ background: "#25D366", color: "#0B1F12" }}
                data-testid="cta-book-whatsapp"
              >
                <MessageCircle className="w-4 h-4" /> Book on WhatsApp
              </a>
              <Link to="/listings" className="btn-pill" style={{ background: "transparent", color: "#fff", border: "1px solid rgba(255,255,255,0.4)" }} data-testid="cta-browse">
                Browse stays
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
